/**
 * Tolerant answer comparison. A 5th grader should not be marked wrong because of
 * a missing accent, a capital letter or an article.
 */
import type { AnswerGrade } from './types';

const ARTICLES = [
  // German
  'der',
  'die',
  'das',
  'den',
  'dem',
  'ein',
  'eine',
  // French
  'le',
  'la',
  'les',
  'un',
  'une',
  'des',
  "l'",
  // English
  'the',
  'a',
  'an',
  // Italian
  'il',
  'lo',
  'gli',
  'una',
];

/** Removes diacritics, punctuation, articles and duplicate whitespace. */
export function normalizeAnswer(value: string): string {
  let text = value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    // Drop hints in brackets, e.g. "pomme (Frucht)".
    .replace(/\([^)]*\)/g, ' ')
    .replace(/\[[^\]]*\]/g, ' ')
    .replace(/[.,!?;:"„“”«»]/g, ' ')
    .replace(/[’´`]/g, "'")
    .replace(/\s+/g, ' ')
    .trim();

  // Strip a leading article ("la maison" -> "maison", "l'ecole" -> "ecole").
  for (const article of ARTICLES) {
    if (article.endsWith("'")) {
      if (text.startsWith(article)) {
        text = text.slice(article.length).trim();
        break;
      }
    } else if (text.startsWith(`${article} `)) {
      text = text.slice(article.length + 1).trim();
      break;
    }
  }

  return text;
}

/** Classic Levenshtein distance, capped for performance on long strings. */
export function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  let previous = Array.from({ length: b.length + 1 }, (_, i) => i);
  const current = new Array<number>(b.length + 1);

  for (let i = 1; i <= a.length; i++) {
    current[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      current[j] = Math.min(current[j - 1] + 1, previous[j] + 1, previous[j - 1] + cost);
    }
    previous = current.slice();
  }

  return previous[b.length];
}

/**
 * Accepts an answer that matches any of the variants separated by "/" or ",",
 * so "maison / demeure" can be answered with either word. The full text is
 * always a variant as well: in the multiple choice mode the pupil taps the
 * whole answer as it is written on the card, including every variant.
 */
function variantsOf(expected: string): string[] {
  const parts = expected
    .split(/\s*[\/,]\s*/)
    .map((part) => normalizeAnswer(part))
    .filter((part) => part.length > 0);

  const whole = normalizeAnswer(expected);
  if (whole !== '' && !parts.includes(whole)) parts.push(whole);

  return parts;
}

export function gradeAnswer(given: string, expected: string): AnswerGrade {
  const normalizedGiven = normalizeAnswer(given);
  if (normalizedGiven === '') return 'wrong';

  const candidates = variantsOf(expected);
  if (candidates.includes(normalizedGiven)) return 'correct';

  // One typo is forgiven, but only for words long enough that a single edit
  // does not turn one real word into a different one.
  for (const candidate of candidates) {
    if (candidate.length >= 4 && levenshtein(normalizedGiven, candidate) <= 1) {
      return 'near';
    }
  }

  return 'wrong';
}
